import React, { useState } from 'react';
import { CloseIcon } from '../../../../public/icons';
import adminService from '../../../../services/admin/admin-authService';
import { useToast } from '../../../../context/ToastContext'; // Adjust path as needed

const AddAdminModal = ({ isOpen, onClose, onAdminAdded }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Operations Manager');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { success, error: showError } = useToast();

  if (!isOpen) return null;

  const roles = [
    'Operations Manager',
    'Super Admin',
    'Support Manager',
  ];

  const handleSubmit = async () => {
    setError('');

    if (!fullName.trim() || !email.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    const adminData = {
      fullName: fullName.trim(),
      email: email.trim(),
      role,
      phoneNumber: phoneNumber.trim(),
    };

    try {
      const response = await adminService.createAdmin(adminData);
      
      if (response.success) {
        // Reset form
        setFullName('');
        setEmail('');
        setRole('Operations Manager');
        setPhoneNumber('');
        setError('');
        
        // Notify parent and show success toast
        if (onAdminAdded) {
          onAdminAdded(response.data);
        }
        
        // Show success toast
        success(`Invitation sent successfully to ${adminData.email}`);
        
        onClose();
      } else {
        setError(response.message || 'Failed to create admin');
        showError(response.message || 'Failed to create admin');
      }
      
    } catch (error) {
      console.error('Error creating admin:', error);
      const errorMsg = error.message || 'Failed to create admin. Please try again.';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFullName('');
    setEmail('');
    setRole('Operations Manager');
    setPhoneNumber('');
    setError('');
    onClose();
  };

  const isSubmitEnabled = fullName.trim().length > 0 && 
                         email.trim().length > 0 && 
                         !isSubmitting;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#fff] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b px-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">Add new Admin</h2>
              <p className="text-[#6B635D] text-sm mt-1">
                Invited admins will receive an email to activate their access.
              </p>
            </div>
            <button 
              onClick={handleClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded"
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-600 rounded">
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Full Name */}
            <div className="mb-6">
              <label className="block text-left text-sm font-medium text-[#1C2A46] mb-2">
                Full Name *
              </label>
              <input
                type="text"
                placeholder="Enter full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full p-3 bg-transparent border border-[#DBDBDB] placeholder:text-sm placeholder:text-[#6B635D] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Email Address */}
            <div className="mb-6">
              <label className="block text-left text-sm font-medium text-[#1C2A46] mb-2">
                Email Address *
              </label>
              <input
                type="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 bg-transparent border border-[#DBDBDB] placeholder:text-sm placeholder:text-[#6B635D] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Assign Role */}
            <div className="mb-6">
              <label className="block text-left text-sm font-medium text-[#1C2A46] mb-2">
                Assign Role *
              </label>
              <div className="relative">
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full p-3 bg-transparent border border-[#DBDBDB] text-[#1C2A46] rounded appearance-none focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent cursor-pointer"
                  disabled={isSubmitting}
                >
                  {roles.map((roleOption) => (
                    <option key={roleOption} value={roleOption}>
                      {roleOption}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-[#6B635D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Phone Number */}
            <div className="mb-8">
              <label className="block text-left text-sm font-medium text-[#1C2A46] mb-2">
                Phone Number (optional)
              </label>
              <div className="flex">
                <div className="flex items-center px-3 bg-[#F5F5F5] border border-r-0 border-[#DBDBDB] rounded-l">
                  <span className="text-[#6B635D] text-sm">+234</span>
                </div>
                <input
                  type="tel"
                  placeholder="Enter your Phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="flex-1 p-3 bg-transparent border border-[#DBDBDB] placeholder:text-sm placeholder:text-[#6B635D] rounded-r focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#DBDBDB]">
            <button 
              onClick={handleSubmit}
              disabled={!isSubmitEnabled}
              className={`w-full py-3 font-medium transition-colors rounded ${
                isSubmitEnabled
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Adding Admin...
                </span>
              ) : 'Add New Admin'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddAdminModal;